import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import MobileLayout from "../components/layout/MobileLayout";
import { ArrowLeft, History, ChevronRight, Wallet } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";

/* =========================================================================
   COIN FLIP — Obsidian × Champagne Gold. Center-stage coin with a circular
   countdown ring. History runs RIGHT-TO-LEFT (latest on the LEFT).
   ========================================================================= */

const CHOICES = [
  { key: "heads", label: "HEADS", short: "H",
    face: "#f4d68a",            // champagne top
    faceDark: "#7a5015",        // rich amber bottom
    ink: "#3d2005",             // engraved letter color
    rim: "#c99b3b",
    glow: "rgba(244,214,138,0.55)" },
  { key: "tails", label: "TAILS", short: "T",
    face: "#e6e6ea",            // pearl
    faceDark: "#5b5b64",
    ink: "#1c1c22",
    rim: "#a7a7ad",
    glow: "rgba(232,232,238,0.55)" },
];

const PRESETS = [50, 100, 500, 1000, 5000];

export default function CoinFlipGamePage() {
  const { user, refreshUser } = useAuth();
  const [round, setRound] = useState(null);
  const [amount, setAmount] = useState(50);
  const [placing, setPlacing] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [landed, setLanded] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const previousRoundIdRef = useRef(null);
  const revealStartedForRoundRef = useRef(null);
  const audioRef = useRef(null);

  const SPIN_DURATION_MS = 4000;

  const fetchRound = useCallback(async () => {
    try {
      const { data } = await api.get(`/games/coin_flip/current`);
      setRound(data);
      // On new round: reset landed / celebration
      if (previousRoundIdRef.current && previousRoundIdRef.current !== data.round_id) {
        setLanded(false);
        setShowCelebration(false);
        revealStartedForRoundRef.current = null;
        refreshUser();
      }
      // Kick off spin timer once per round when reveal begins
      if (!data.betting_open && data.result && revealStartedForRoundRef.current !== data.round_id) {
        revealStartedForRoundRef.current = data.round_id;
        setLanded(false);
        setShowCelebration(false);
        try { audioRef.current?.play?.().catch(() => {}); } catch (_) {}
        setTimeout(() => {
          setLanded(true);
          setShowCelebration(true);
        }, SPIN_DURATION_MS);
      }
      previousRoundIdRef.current = data.round_id;
    } catch (e) { console.error(e); }
  }, [refreshUser]);

  useEffect(() => {
    fetchRound();
    const id = setInterval(fetchRound, 1000);
    return () => clearInterval(id);
  }, [fetchRound]);

  const placeBet = async (choice) => {
    if (!round?.betting_open) return toast.error("Betting is locked");
    if (amount < (round?.min_bet || 50)) return toast.error(`Min bet ₹${round?.min_bet || 50}`);
    if ((user?.wallet_balance ?? 0) < amount) return toast.error("Insufficient wallet balance");
    setPlacing(true);
    try {
      await api.post(`/games/coin_flip/bet`, { choice, amount });
      toast.success(`₹${amount} on ${choice.toUpperCase()} placed!`);
      refreshUser();
      fetchRound();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPlacing(false); }
  };

  const myBetsByChoice = useMemo(() => {
    const map = { heads: 0, tails: 0 };
    (round?.my_bets || []).forEach(b => { map[b.choice] = (map[b.choice] || 0) + (b.amount || 0); });
    return map;
  }, [round]);

  if (!round) return (
    <MobileLayout>
      <div className="h-64 flex items-center justify-center text-amber-200/70 tracking-widest text-xs">LOADING…</div>
    </MobileLayout>
  );

  const bettingRemainingSec = round.betting_time_left != null
    ? round.betting_time_left
    : Math.max(0, round.time_left - (round.round_duration - round.betting_window));
  const inReveal = !round.betting_open;
  const currentResult = inReveal ? round.result : null;
  // Winner is only "revealed" to the UI once the coin has landed
  const winnerResult = landed ? currentResult : null;
  const timerSec = Math.max(0, Math.ceil(bettingRemainingSec));
  const timerFraction = Math.max(0, Math.min(1, bettingRemainingSec / round.betting_window));
  const criticalTime = round.betting_open && bettingRemainingSec <= 5;

  // Coin rests on LAST winner's face during betting (until this round's spin starts)
  const lastWinner = round.history?.[0]?.result || "heads";

  return (
    <MobileLayout>
      <Toaster richColors position="top-center" />
      <audio ref={audioRef} src="/games/card-flip.mp3" preload="auto" />

      {/* OBSIDIAN BACKGROUND with warm spotlight */}
      <div
        className="absolute left-0 right-0 top-16 bottom-0 -z-0 pointer-events-none"
        style={{
          background: "radial-gradient(circle at 50% 28%, #1a1410 0%, #0a0708 45%, #030303 100%)",
        }}
      />
      {/* Ultra-fine grain */}
      <div
        className="absolute left-0 right-0 top-16 bottom-0 -z-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(rgba(244,214,138,0.7) 0.5px, transparent 0.5px)",
          backgroundSize: "2.5px 2.5px",
        }}
      />

      <div className="relative z-10">
        {/* THIN LUXE HEADER */}
        <div className="sticky top-0 z-30 -mx-3 px-3 py-2.5 mb-3 backdrop-blur-2xl bg-black/50 border-b border-amber-300/15 flex items-center justify-between">
          <Link to="/" data-testid="back-btn" className="w-8 h-8 rounded-full bg-white/5 border border-amber-300/25 flex items-center justify-center text-amber-200 active:scale-95 transition-transform">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex flex-col items-center leading-none">
            <span
              className="font-display font-black tracking-[0.35em] text-[11px]"
              style={{ color: "#e5c07a", textShadow: "0 0 12px rgba(244,214,138,0.4)" }}
            >
              COIN · FLIP
            </span>
            <span className="text-[8px] tracking-[0.4em] text-amber-200/40 mt-0.5">R#{round.round_number}</span>
          </div>
          <div
            data-testid="wallet-chip"
            className="flex items-center gap-1.5 rounded-full bg-white/[0.04] border border-amber-300/30 px-2.5 py-1 backdrop-blur-md"
          >
            <Wallet className="w-3 h-3 text-amber-200" />
            <span className="tabular-nums text-[11px] font-black" style={{ color: "#f4d68a" }}>₹{user?.wallet_balance ?? 0}</span>
          </div>
        </div>

        {/* THE COIN ARENA — coin with circular timer ring around it */}
        <div
          data-testid="round-card"
          className="relative flex flex-col items-center py-3 mb-3"
        >
          {/* Ambient champagne glow behind the coin */}
          <div
            className="absolute pointer-events-none rounded-full"
            style={{
              width: "260px", height: "260px",
              background: "radial-gradient(circle, rgba(244,214,138,0.16) 0%, transparent 65%)",
              top: "-10px",
            }}
          />
          <RingCoinCombo
            fraction={timerFraction}
            criticalTime={criticalTime}
            spinning={inReveal && !!currentResult && !landed}
            landed={landed}
            revealResult={currentResult}
            betOpen={round.betting_open}
            lastWinner={lastWinner}
            spinDurationMs={SPIN_DURATION_MS}
          />
          {/* Celebration burst — only after coin has landed */}
          {showCelebration && currentResult && <CelebrationBurst result={currentResult} />}
          {/* Timer / status text below coin */}
          <div className="mt-3 flex flex-col items-center gap-1.5">
            {round.betting_open ? (
              <>
                <div className="text-[9px] tracking-[0.4em] font-black text-amber-200/60">TIME REMAINING</div>
                <div
                  data-testid="countdown"
                  className={`font-display font-black tabular-nums leading-none ${criticalTime ? "animate-pulse" : ""}`}
                  style={{
                    fontSize: "40px",
                    color: criticalTime ? "#fda4af" : "#f4d68a",
                    textShadow: criticalTime ? "0 0 22px rgba(251,113,133,0.65)" : "0 0 22px rgba(244,214,138,0.5)",
                  }}
                >
                  {timerSec}<span className="text-lg opacity-70 ml-0.5">s</span>
                </div>
                <div data-testid="round-number" className="sr-only">#{round.round_number}</div>
              </>
            ) : (
              <>
                <div className="text-[9px] tracking-[0.4em] font-black text-amber-200/60">
                  {landed && winnerResult ? "WINNER" : "SPINNING"}
                </div>
                {landed && winnerResult ? (
                  <div
                    data-testid="countdown"
                    className="font-display font-black tracking-[0.25em] text-3xl animate-pulse"
                    style={{
                      color: winnerResult === "heads" ? "#f4d68a" : "#e6e6ea",
                      textShadow: `0 0 32px ${winnerResult === "heads" ? "rgba(244,214,138,0.9)" : "rgba(232,232,238,0.9)"}`,
                    }}
                  >
                    🎉 {winnerResult.toUpperCase()} WON 🎉
                  </div>
                ) : (
                  <div data-testid="countdown" className="font-display font-black tracking-[0.4em] text-lg text-amber-200/80 animate-pulse">
                    ▪ ▪ ▪
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* HEADS / TAILS — elegant metallic pills */}
        <div className="grid grid-cols-2 gap-2.5 mb-3" data-testid="choice-grid">
          {CHOICES.map((c) => {
            const myBet = myBetsByChoice[c.key];
            const isWinner = landed && winnerResult === c.key;
            const disabled = !round.betting_open || placing;
            return (
              <MetalButton
                key={c.key}
                choice={c}
                myBet={myBet}
                isWinner={isWinner}
                disabled={disabled}
                betAmount={amount}
                onPlace={() => placeBet(c.key)}
                locked={!round.betting_open}
              />
            );
          })}
        </div>

        {/* BET AMOUNT */}
        <div
          data-testid="amount-selector"
          className="rounded-xl bg-white/[0.03] backdrop-blur-xl border border-amber-300/15 p-2.5 mb-3"
        >
          <div className="flex items-center gap-2 mb-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-black text-[13px]"
              style={{
                background: "radial-gradient(circle at 30% 30%, #f4d68a, #7a5015)",
                color: "#3d2005",
                boxShadow: "inset 0 -1px 3px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.4)",
              }}
            >
              ₹
            </div>
            <input
              type="number"
              min={round.min_bet}
              value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
              data-testid="amount-input"
              className="flex-1 min-w-0 text-lg font-black text-amber-100 tabular-nums bg-transparent outline-none placeholder:text-white/30"
            />
            <div className="text-[9px] text-amber-200/40 tracking-[0.2em] shrink-0">MIN ₹{round.min_bet}</div>
          </div>
          <div className="grid grid-cols-5 gap-1">
            {PRESETS.map(v => {
              const active = amount === v;
              return (
                <button
                  key={v}
                  onClick={() => setAmount(v)}
                  data-testid={`preset-${v}`}
                  className={`h-8 rounded-lg font-black text-[11px] tabular-nums transition-transform active:scale-90`}
                  style={{
                    background: active ? "linear-gradient(135deg, #f4d68a, #b57d1e)" : "rgba(255,255,255,0.03)",
                    border: active ? "1px solid #f4d68a" : "1px solid rgba(244,214,138,0.15)",
                    color: active ? "#3d2005" : "#f4d68a",
                    boxShadow: active ? "0 0 10px rgba(244,214,138,0.4)" : "none",
                  }}
                >
                  {v >= 1000 ? `${v/1000}K` : v}
                </button>
              );
            })}
          </div>
        </div>

        {/* HISTORY — LATEST arrow on LEFT, right-to-left flow */}
        <div
          data-testid="history-strip"
          className="rounded-xl bg-white/[0.03] backdrop-blur-xl border border-amber-300/15 p-2.5 mb-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.3em] text-amber-200/70 font-black">
              <History className="w-3 h-3" /> HISTORY
            </div>
            <button
              onClick={() => setShowHistoryModal(true)}
              data-testid="game-history-btn"
              className="flex items-center gap-0.5 text-[9px] font-black tracking-wider text-amber-200/80"
            >
              ALL <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* LATEST indicator pill */}
            <div
              className="shrink-0 text-[8px] font-black tracking-[0.2em] px-1.5 py-1 rounded flex flex-col items-center leading-none"
              style={{
                background: "linear-gradient(180deg, rgba(244,214,138,0.25), rgba(244,214,138,0.05))",
                border: "1px solid rgba(244,214,138,0.4)",
                color: "#f4d68a",
              }}
            >
              LATEST<br/><span className="text-[10px] mt-0.5">◀</span>
            </div>
            {/* Latest first (already sorted by API); flows right to left visually */}
            <div className="flex-1 flex gap-1.5 overflow-x-auto pb-0.5">
              {round.history?.map((h, i) => {
                const c = CHOICES.find(x => x.key === h.result) || CHOICES[0];
                const isLatest = i === 0;
                return (
                  <div
                    key={i}
                    data-testid={`history-${i}`}
                    className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-black text-[11px]"
                    style={{
                      background: `radial-gradient(circle at 30% 30%, ${c.face}, ${c.faceDark})`,
                      border: isLatest ? `1.5px solid #f4d68a` : `1px solid ${c.rim}`,
                      color: c.ink,
                      boxShadow: isLatest
                        ? `0 0 12px ${c.glow}, inset 0 -1px 3px rgba(0,0,0,0.4)`
                        : `inset 0 -1px 3px rgba(0,0,0,0.4), inset 0 1px 2px rgba(255,255,255,0.3)`,
                    }}
                    title={`#${h.round_number} · ${c.label}`}
                  >
                    {c.short}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {showHistoryModal && (
          <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-end animate-fade-in" onClick={() => setShowHistoryModal(false)}>
            <div className="w-full max-w-md mx-auto bg-gradient-to-b from-[#1a1410] to-black border-t-2 border-amber-300/60 rounded-t-3xl p-4 max-h-[75vh] overflow-y-auto pb-24" onClick={e => e.stopPropagation()}>
              <div className="text-center mb-4">
                <div className="font-display font-black text-xl tracking-[0.2em] text-amber-200">FLIP HISTORY</div>
                <div className="text-[10px] tracking-widest text-amber-200/50 mt-1">Latest first</div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {round.history?.map((h, i) => {
                  const c = CHOICES.find(x => x.key === h.result) || CHOICES[0];
                  return (
                    <div key={i} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-white/5 border border-amber-300/15">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center font-black text-lg"
                        style={{
                          background: `radial-gradient(circle at 30% 30%, ${c.face}, ${c.faceDark})`,
                          border: `1.5px solid ${c.rim}`,
                          color: c.ink,
                        }}
                      >
                        {c.short}
                      </div>
                      <div className="text-[9px] font-black text-amber-200 tracking-wider">#{h.round_number}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}

/* -------------------------------------------------------------------------
   RingCoinCombo — circular timer ring + a coin that spins LEFT-RIGHT on
   the Y-axis. During BETTING the coin shows a NEUTRAL face ("?") — no letter
   is leaked. On reveal it spins ~8 full rotations for `spinDurationMs` then
   lands on the correct face (front = H, back = T).
   ------------------------------------------------------------------------- */
function RingCoinCombo({ fraction, criticalTime, spinning, landed, revealResult, betOpen, lastWinner, spinDurationMs = 4000 }) {
  const [spinAngle, setSpinAngle] = useState(lastWinner === "tails" ? 180 : 0);

  useEffect(() => {
    if (!spinning || !revealResult) {
      // Rest on last winner's face (front=H at 0°, back=T at 180°)
      if (!landed) setSpinAngle(lastWinner === "tails" ? 180 : 0);
      return;
    }
    // Spin: 8 full rotations from current base + land on correct face
    const landingAngle = revealResult === "heads" ? 0 : 180;
    const finalAngle = 8 * 360 + landingAngle;
    requestAnimationFrame(() => setSpinAngle(finalAngle));
  }, [spinning, revealResult, landed, lastWinner]);

  // Ring geometry
  const RING_SIZE = 220;
  const STROKE = 3;
  const R = (RING_SIZE - STROKE * 2) / 2;
  const C = 2 * Math.PI * R;
  const dashOffset = C * (1 - fraction);

  const ringColor = criticalTime ? "#fda4af" : "#f4d68a";
  const ringTrack = "rgba(244,214,138,0.08)";

  const winner = landed ? (revealResult === "heads" ? CHOICES[0] : CHOICES[1]) : null;

  return (
    <div className="relative flex items-center justify-center" style={{ width: RING_SIZE, height: RING_SIZE }}>
      {/* SVG timer ring */}
      <svg width={RING_SIZE} height={RING_SIZE} className="absolute inset-0" style={{ transform: "rotate(-90deg)" }}>
        <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={R} fill="none" stroke={ringTrack} strokeWidth={STROKE} />
        <circle
          cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={R}
          fill="none" stroke={ringColor} strokeWidth={STROKE} strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={betOpen ? dashOffset : C}
          style={{
            transition: "stroke-dashoffset 800ms linear, stroke 400ms ease",
            filter: `drop-shadow(0 0 6px ${criticalTime ? "rgba(251,113,133,0.7)" : "rgba(244,214,138,0.6)"})`,
          }}
        />
      </svg>

      {/* Coin container with perspective + preserve-3d */}
      <div
        className="relative"
        style={{
          width: 160,
          height: 160,
          perspective: "1400px",
          animation: spinning ? "none" : (landed ? "coinPop 500ms ease-out" : "coinBob 3.2s ease-in-out infinite"),
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            transformStyle: "preserve-3d",
            transform: `rotateY(${spinAngle}deg)`,
            transition: spinning ? `transform ${spinDurationMs}ms cubic-bezier(0.15, 0.55, 0.25, 1)` : "transform 400ms ease",
          }}
        >
          {/* FRONT face — HEADS */}
          <CoinFace
            side="heads"
            letter="H"
            highlight={landed && revealResult === "heads"}
            isBack={false}
          />
          {/* BACK face — TAILS */}
          <CoinFace
            side="tails"
            letter="T"
            highlight={landed && revealResult === "tails"}
            isBack={true}
          />
          {/* Rim strip visible during spin (edge of coin) */}
          <div
            style={{
              position: "absolute",
              width: 6,
              height: 160,
              left: "calc(50% - 3px)",
              top: 0,
              background: "linear-gradient(180deg, #7a5015, #f4d68a 20%, #7a5015 40%, #f4d68a 60%, #7a5015 80%, #f4d68a)",
              transform: "rotateY(90deg) translateZ(0)",
              borderRadius: 1,
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes coinBob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @keyframes coinPop {
          0%   { transform: scale(1.28); }
          60%  { transform: scale(0.95); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function CoinFace({ side, letter, highlight, isBack }) {
  const c = side === "heads" ? CHOICES[0] : CHOICES[1];
  return (
    <div
      className="absolute inset-0 rounded-full flex items-center justify-center font-display font-black select-none"
      style={{
        background: `radial-gradient(circle at 32% 28%, ${c.face} 0%, ${c.face} 12%, ${c.faceDark} 78%, #000 100%)`,
        border: `3px solid ${highlight ? "#fef3c7" : c.rim}`,
        color: c.ink,
        fontSize: "96px",
        lineHeight: 1,
        boxShadow: `0 20px 40px rgba(0,0,0,0.7), 0 0 ${highlight ? "50px" : "22px"} ${c.glow}, inset 0 -8px 14px rgba(0,0,0,0.5), inset 0 8px 14px rgba(255,255,255,0.35)`,
        textShadow: `0 2px 0 rgba(0,0,0,0.35), 0 -1px 0 rgba(255,255,255,0.28)`,
        backfaceVisibility: "hidden",
        transform: isBack ? "rotateY(180deg)" : "rotateY(0deg)",
      }}
    >
      <span
        className="absolute rounded-full pointer-events-none"
        style={{
          inset: "12px",
          border: `1.5px solid rgba(0,0,0,0.35)`,
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.15)`,
        }}
      />
      <span
        className="absolute rounded-full pointer-events-none"
        style={{ inset: "16px", border: `1px dashed ${c.ink}55` }}
      />
      {letter}
    </div>
  );
}

/* -------------------------------------------------------------------------
   CelebrationBurst — gold particle burst + "WIN" flash. Fires once when
   coin lands. CSS-only particles radiating outward.
   ------------------------------------------------------------------------- */
function CelebrationBurst({ result }) {
  const PARTICLES = 22;
  const isHeads = result === "heads";
  const color = isHeads ? "#f4d68a" : "#e6e6ea";
  const color2 = isHeads ? "#b57d1e" : "#8a8a92";
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-visible" style={{ zIndex: 20 }}>
      {/* Radial gold rays */}
      <div
        className="absolute rounded-full"
        style={{
          width: 300, height: 300,
          background: `radial-gradient(circle, ${color}40 0%, transparent 55%)`,
          animation: "burstFade 1200ms ease-out forwards",
        }}
      />
      {/* Particles */}
      {Array.from({ length: PARTICLES }).map((_, i) => {
        const angle = (i / PARTICLES) * 360;
        const dist = 90 + (i % 3) * 25; // 90..140px
        const size = 6 + (i % 3) * 2;
        const rad = (angle * Math.PI) / 180;
        const dx = Math.cos(rad) * dist;
        const dy = Math.sin(rad) * dist;
        return (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              width: size,
              height: size,
              background: `linear-gradient(135deg, ${color}, ${color2})`,
              boxShadow: `0 0 8px ${color}`,
              animation: `particleFly 1000ms cubic-bezier(0.15, 0.85, 0.35, 1) forwards`,
              // CSS var → keyframe uses it for translate
              ["--dx"]: `${dx}px`,
              ["--dy"]: `${dy}px`,
            }}
          />
        );
      })}
      {/* WIN flash text */}
      <div
        className="absolute font-display font-black tracking-[0.25em] text-4xl"
        style={{
          color,
          textShadow: `0 0 26px ${color}, 0 0 60px ${color}55`,
          animation: "winFlash 1400ms ease-out forwards",
          top: "-30px",
        }}
      >
        WIN!
      </div>
      <style>{`
        @keyframes particleFly {
          0%   { transform: translate(0, 0) scale(0.4); opacity: 0; }
          20%  { transform: translate(calc(var(--dx) * 0.25), calc(var(--dy) * 0.25)) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0.4); opacity: 0; }
        }
        @keyframes winFlash {
          0%   { transform: scale(0.3); opacity: 0; }
          20%  { transform: scale(1.25); opacity: 1; }
          70%  { transform: scale(1); opacity: 1; }
          100% { transform: scale(1); opacity: 0; }
        }
        @keyframes burstFade {
          0%   { transform: scale(0.4); opacity: 0.9; }
          100% { transform: scale(1.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/* -------------------------------------------------------------------------
   MetalButton — elegant horizontal pill with mini-coin emblem + label + MY BET
   ------------------------------------------------------------------------- */
function MetalButton({ choice, myBet, isWinner, disabled, betAmount, onPlace, locked }) {
  const hasBet = myBet > 0;
  return (
    <button
      onClick={onPlace}
      disabled={disabled}
      data-testid={`choice-${choice.key}`}
      className={`relative rounded-2xl h-[86px] px-3 flex items-center gap-3 transition-transform active:scale-95 disabled:active:scale-100 overflow-hidden`}
      style={{
        background: `linear-gradient(160deg, ${choice.face} 0%, ${choice.faceDark} 100%)`,
        border: `1.5px solid ${isWinner ? "#fef3c7" : choice.rim}`,
        boxShadow: isWinner
          ? `0 0 32px ${choice.glow}, inset 0 0 20px rgba(255,255,255,0.2)`
          : `0 10px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -2px 4px rgba(0,0,0,0.3)`,
        opacity: disabled && !hasBet ? 0.6 : 1,
      }}
    >
      {/* Glossy sheen */}
      <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent pointer-events-none rounded-t-2xl" />
      {/* 2× badge top-right */}
      <div
        className="absolute top-1.5 right-2 text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded-full"
        style={{
          background: "rgba(0,0,0,0.45)",
          border: `1px solid ${choice.rim}`,
          color: choice.ink,
          filter: "brightness(1.6)",
        }}
      >
        2×
      </div>

      {/* Mini coin emblem */}
      <div
        className="relative w-14 h-14 rounded-full flex items-center justify-center shrink-0 font-display font-black"
        style={{
          background: `radial-gradient(circle at 30% 30%, ${choice.face}, ${choice.faceDark})`,
          border: `2px solid ${choice.rim}`,
          color: choice.ink,
          fontSize: "30px",
          lineHeight: 1,
          boxShadow: "inset 0 -3px 8px rgba(0,0,0,0.4), inset 0 3px 6px rgba(255,255,255,0.4), 0 4px 10px rgba(0,0,0,0.35)",
          textShadow: "0 1px 0 rgba(0,0,0,0.3), 0 -1px 0 rgba(255,255,255,0.25)",
        }}
      >
        <span
          className="absolute rounded-full pointer-events-none"
          style={{ inset: "5px", border: `1px dashed ${choice.ink}44` }}
        />
        {choice.short}
      </div>

      <div className="flex-1 min-w-0 flex flex-col items-start justify-center gap-1">
        <div
          className="font-display font-black text-[16px] tracking-[0.3em]"
          style={{ color: choice.ink, textShadow: `0 1px 0 rgba(255,255,255,0.35), 0 -1px 0 rgba(0,0,0,0.35)` }}
        >
          {choice.label}
        </div>
        {hasBet && (
          <div
            data-testid={`my-bet-${choice.key}`}
            className="text-[11px] font-black tracking-wider bg-emerald-500 text-white rounded-full px-2.5 py-0.5 shadow-md border border-white/30"
          >
            ₹{myBet}
          </div>
        )}
      </div>
    </button>
  );
}
